import { ColorRepresentation } from 'three';

import { Line3D, LineSegment3D, Point2D, Polygon3D } from '@paragon/geometry-library';
import { BoundedPlaneData } from '@paragon/layout';

import { clipLine3DAroundPoint } from '../../../util/infiniteLines';

type EdgeData = { dashed: boolean; color: ColorRepresentation };

type EdgeLine = EdgeData & { line: Line3D };
type EdgeSegment = EdgeData & { segment: LineSegment3D };

/**
 * The input to a `LayoutPolygonsEdgesRenderer` component, which is responsible for rendering the edges of a set of
 *   layout polygons. Use this to collect edges from all relevant sources and then pass it to the renderer.
 */
export class LayoutPolygonsEdgesCollection {
  private _lines: EdgeLine[] = [];
  private _segments: EdgeSegment[] = [];

  hasLines() {
    return this._lines.length > 0;
  }

  getSegments(viewCenter: Point2D): EdgeSegment[] {
    return [
      ...this._segments,
      ...this._lines.map(({ line, color }) => ({
        segment: clipLine3DAroundPoint(line, viewCenter),
        color,
        dashed: true,
      })),
    ];
  }

  add(geometry: Line3D | LineSegment3D, color: ColorRepresentation) {
    if (geometry instanceof Line3D) {
      this.addLine(geometry, color);
    } else {
      this.addSegment(geometry, color);
    }
  }

  addSegment(geometry: LineSegment3D, color: ColorRepresentation) {
    this._segments.push({ segment: geometry, color, dashed: false });
  }

  addDashedSegment(geometry: LineSegment3D, color: ColorRepresentation) {
    this._segments.push({ segment: geometry, color, dashed: true });
  }

  addLine(geometry: Line3D, color: ColorRepresentation) {
    this._lines.push({ line: geometry, dashed: true, color });
  }

  addPolygonEdges(polygon: Polygon3D, color: ColorRepresentation) {
    for (const geometry of polygon.edges) {
      this.addSegment(geometry, color);
    }
  }

  addFromBounds(bounds: BoundedPlaneData, color: ColorRepresentation) {
    if (!bounds.inProgress) {
      for (const shape of bounds.shape) {
        this.addPolygonEdges(shape, color);
      }
    } else {
      for (const segment of bounds.shape.segments) {
        this.addSegment(segment, color);
      }

      for (const line of bounds.shape.lines) {
        this.addLine(line, color);
      }

      for (const dashedSegment of bounds.shape.dashedSegments) {
        this.addDashedSegment(dashedSegment, color);
      }
    }
  }
}
